import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { RefreshCw, PlusCircle, Mail, Edit, Trash2, Eye } from 'lucide-react';

const EMAIL_CHECK_TYPES = [
  { value: 'address_check', label: 'Address Verification', color: 'bg-blue-100 text-blue-700' },
  { value: 'employment_check', label: 'Employment Verification', color: 'bg-green-100 text-green-700' },
  { value: 'education_check', label: 'Education Verification', color: 'bg-purple-100 text-purple-700' },
  { value: 'criminal_court_check', label: 'Criminal Court Check', color: 'bg-red-100 text-red-700' },
  { value: 'criminal_advocate_check', label: 'Criminal Advocate Check', color: 'bg-orange-100 text-orange-700' },
  { value: 'id_check', label: 'ID Verification', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'world_criminal_db', label: 'World Database Check', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'drug_test', label: 'Drug Test', color: 'bg-pink-100 text-pink-700' },
  { value: 'credit_check', label: 'Credit/CIBIL Check', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'uan_check', label: 'UAN Verification', color: 'bg-teal-100 text-teal-700' },
];

const TABLE_COLUMN_OPTIONS = {
  address_check: ['case_id', 'employee_name', 'father_name', 'dob', 'address', 'city', 'state', 'pincode', 'contact_number', 'additional_contact', 'remarks', 'tat_days', 'days_left'],
  criminal_court_check: ['case_id', 'employee_name', 'father_name', 'dob', 'address', 'city', 'state', 'pincode', 'contact_number', 'additional_contact', 'remarks'],
  criminal_advocate_check: ['case_id', 'employee_name', 'father_name', 'dob', 'address', 'city', 'state', 'pincode', 'contact_number', 'additional_contact', 'remarks'],
  employment_check: ['case_id', 'employee_name', 'company_name', 'company_address', 'designation', 'period_of_employment', 'contact_person', 'contact_number', 'remarks'],
  education_check: ['case_id', 'employee_name', 'university_name', 'university_address', 'course', 'year_of_passing', 'roll_number', 'contact_number', 'remarks'],
  id_check: ['case_id', 'employee_name', 'id_type', 'id_number', 'dob', 'contact_number', 'remarks'],
  world_criminal_db: ['case_id', 'employee_name', 'id_number', 'passport_number', 'dob', 'contact_number', 'remarks'],
  drug_test: ['case_id', 'employee_name', 'dob', 'contact_number', 'test_center', 'remarks'],
  credit_check: ['case_id', 'employee_name', 'pan_number', 'dob', 'contact_number', 'remarks'],
  uan_check: ['case_id', 'employee_name', 'company_name', 'company_address', 'uan_number', 'contact_number', 'remarks'],
};

const ATTACHMENT_OPTIONS = [
  { value: 'verification_letter', label: 'Verification Request Letter' },
  { value: 'candidate_documents', label: 'Candidate Submitted Documents' },
  { value: 'id_proof', label: 'ID Proofs (PAN/Aadhar)' },
];

const getCheckTypeLabel = (value) => {
  const found = EMAIL_CHECK_TYPES.find(t => t.value === value);
  return found ? found.label : value?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const getCheckTypeColor = (value) => {
  const found = EMAIL_CHECK_TYPES.find(t => t.value === value);
  return found ? found.color : 'bg-gray-100 text-gray-700';
};

export const EmailTemplatesTab = () => {
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [templateFormData, setTemplateFormData] = useState({
    name: '',
    check_type: '',
    subject: '',
    body: '',
    instructions: '',
    include_case_table: true,
    include_attachments: true,
    attachment_types: [],
    table_columns: []
  });
  const [templatePreview, setTemplatePreview] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const fetchEmailTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await axios.get('/verification-templates');
      setEmailTemplates(res.data || []);
    } catch (error) {
      console.error('Failed to fetch email templates:', error);
      toast.error('Failed to load email templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    fetchEmailTemplates();
  }, []);

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateFormData({
      name: template.name || '',
      check_type: template.check_type || '',
      subject: template.subject || '',
      body: template.body || '',
      instructions: template.instructions || '',
      include_case_table: template.include_case_table !== false,
      include_attachments: template.include_attachments !== false,
      attachment_types: template.attachment_types || [],
      table_columns: template.table_columns || []
    });
    setShowTemplateEditor(true);
  };

  const handleSaveTemplate = async () => {
    try {
      if (selectedTemplate?.id?.startsWith('default_') || !selectedTemplate) {
        await axios.post('/verification-templates', templateFormData);
        toast.success('Template created successfully');
      } else {
        await axios.put(`/verification-templates/${selectedTemplate.id}`, templateFormData);
        toast.success('Template updated successfully');
      }
      setShowTemplateEditor(false);
      setSelectedTemplate(null);
      fetchEmailTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handlePreviewTemplate = async () => {
    try {
      const formData = new FormData();
      formData.append('check_type', templateFormData.check_type);
      if (selectedTemplate?.id && !selectedTemplate.id.startsWith('default_')) {
        formData.append('template_id', selectedTemplate.id);
      }
      
      const res = await axios.post('/verification-templates/preview', formData);
      setTemplatePreview(res.data);
    } catch (error) {
      toast.error('Failed to generate preview');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (templateId.startsWith('default_')) {
      toast.error('Cannot delete default templates');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`/verification-templates/${templateId}`);
      toast.success('Template deleted');
      fetchEmailTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="email-templates-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-sky-700">Email Templates</h2>
          <p className="text-slate-500 text-sm">Customize verification assignment emails for each check type</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={fetchEmailTemplates}
            disabled={loadingTemplates}
            data-testid="refresh-templates-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loadingTemplates ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            onClick={() => {
              setSelectedTemplate(null);
              setTemplateFormData({
                name: '',
                check_type: '',
                subject: '',
                body: '',
                instructions: '',
                include_case_table: true,
                include_attachments: true,
                attachment_types: [],
                table_columns: []
              });
              setShowTemplateEditor(true);
            }}
            className="bg-sky-600 hover:bg-sky-700"
            data-testid="create-template-btn"
          >
            <PlusCircle className="w-4 h-4 mr-2" />
            Create New Template
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-sky-50 border-sky-200">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-sky-700">{emailTemplates.length}</div>
            <div className="text-xs text-sky-600">Total Templates</div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-700">{emailTemplates.filter(t => !t.is_default).length}</div>
            <div className="text-xs text-green-600">Custom Templates</div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-700">{emailTemplates.filter(t => t.is_default).length}</div>
            <div className="text-xs text-blue-600">Default Templates</div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-purple-700">{EMAIL_CHECK_TYPES.length}</div>
            <div className="text-xs text-purple-600">Check Types</div>
          </CardContent>
        </Card>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-2 gap-4">
        {loadingTemplates ? (
          <Card className="col-span-2">
            <CardContent className="p-8 text-center">
              <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin text-sky-500" />
              <p className="text-slate-500">Loading templates...</p>
            </CardContent>
          </Card>
        ) : emailTemplates.length === 0 ? (
          <Card className="col-span-2">
            <CardContent className="p-8 text-center">
              <Mail className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">No templates found. Click "Create New Template" to get started.</p>
            </CardContent>
          </Card>
        ) : (
          emailTemplates.map((template) => (
            <Card key={template.id} className="hover:shadow-md transition-shadow border-l-4 border-l-sky-400" data-testid={`template-card-${template.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-800">{template.name}</h3>
                      {template.is_default && (
                        <Badge className="bg-blue-100 text-blue-700 text-xs">Default</Badge>
                      )}
                    </div>
                    <Badge className={getCheckTypeColor(template.check_type)}>
                      {getCheckTypeLabel(template.check_type)}
                    </Badge>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 w-8 p-0 text-sky-600"
                      onClick={() => handleEditTemplate(template)}
                      data-testid={`edit-template-${template.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    {!template.is_default && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0 text-red-500"
                        onClick={() => handleDeleteTemplate(template.id)}
                        data-testid={`delete-template-${template.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="bg-slate-50 p-2 rounded">
                    <span className="text-slate-500">Subject:</span>
                    <p className="text-slate-700 truncate">{template.subject || 'Using default subject'}</p>
                  </div>
                  
                  <div className="flex flex-wrap gap-1">
                    {template.include_case_table && (
                      <Badge variant="outline" className="text-xs">📊 Case Table</Badge>
                    )}
                    {template.include_attachments && (
                      <Badge variant="outline" className="text-xs">📎 Attachments</Badge>
                    )}
                    {template.attachment_types?.map((at) => (
                      <Badge key={at} variant="outline" className="text-xs bg-gray-50">
                        {ATTACHMENT_OPTIONS.find(o => o.value === at)?.label || at}
                      </Badge>
                    ))}
                  </div>
                  
                  {template.instructions && (
                    <p className="text-xs text-slate-500 line-clamp-2">
                      📋 {template.instructions.substring(0, 100)}...
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Template Editor Dialog */}
      <Dialog open={showTemplateEditor} onOpenChange={setShowTemplateEditor}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-sky-600" />
              {selectedTemplate ? 'Edit Template' : 'Create New Template'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column - Form */}
            <div className="space-y-4">
              <div>
                <Label>Template Name *</Label>
                <Input
                  value={templateFormData.name}
                  onChange={(e) => setTemplateFormData({...templateFormData, name: e.target.value})}
                  placeholder="e.g., Address Verification Assignment"
                  data-testid="template-name-input"
                />
              </div>

              <div>
                <Label>Check Type *</Label>
                <Select
                  value={templateFormData.check_type}
                  onValueChange={(v) => {
                    const columns = TABLE_COLUMN_OPTIONS[v] || [];
                    setTemplateFormData({
                      ...templateFormData, 
                      check_type: v,
                      table_columns: columns.slice(0, 8)
                    });
                  }}
                >
                  <SelectTrigger data-testid="check-type-select">
                    <SelectValue placeholder="Select check type" />
                  </SelectTrigger>
                  <SelectContent>
                    {EMAIL_CHECK_TYPES.map((ct) => (
                      <SelectItem key={ct.value} value={ct.value}>
                        {ct.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Email Subject</Label>
                <Input
                  value={templateFormData.subject}
                  onChange={(e) => setTemplateFormData({...templateFormData, subject: e.target.value})}
                  placeholder="e.g., Address Verification - {{employee_name}} (Case: {{case_id}})"
                  data-testid="template-subject-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Use placeholders: {'{{employee_name}}'}, {'{{case_id}}'}, {'{{company_name}}'}, etc.
                </p>
              </div>

              <div>
                <Label>Instructions (will be shown as numbered list)</Label>
                <Textarea
                  value={templateFormData.instructions}
                  onChange={(e) => setTemplateFormData({...templateFormData, instructions: e.target.value})}
                  placeholder="1. Take appointment before visit&#10;2. Carry verification letter&#10;3. Take photos..."
                  rows={6}
                  data-testid="template-instructions-input"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="include_table"
                    checked={templateFormData.include_case_table}
                    onCheckedChange={(checked) => setTemplateFormData({...templateFormData, include_case_table: checked})}
                  />
                  <label htmlFor="include_table" className="text-sm">Include case details table</label>
                </div>
                
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="include_attachments"
                    checked={templateFormData.include_attachments}
                    onCheckedChange={(checked) => setTemplateFormData({...templateFormData, include_attachments: checked})}
                  />
                  <label htmlFor="include_attachments" className="text-sm">Include dynamic attachments</label>
                </div>
              </div>

              {templateFormData.include_attachments && (
                <div>
                  <Label>Attachment Types</Label>
                  <div className="space-y-2 mt-2">
                    {ATTACHMENT_OPTIONS.map((opt) => (
                      <div key={opt.value} className="flex items-center gap-2">
                        <Checkbox
                          id={`att_${opt.value}`}
                          checked={templateFormData.attachment_types?.includes(opt.value)}
                          onCheckedChange={(checked) => {
                            const current = templateFormData.attachment_types || [];
                            if (checked) {
                              setTemplateFormData({...templateFormData, attachment_types: [...current, opt.value]});
                            } else {
                              setTemplateFormData({...templateFormData, attachment_types: current.filter(a => a !== opt.value)});
                            }
                          }}
                        />
                        <label htmlFor={`att_${opt.value}`} className="text-sm">{opt.label}</label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {templateFormData.include_case_table && templateFormData.check_type && (
                <div>
                  <Label>Table Columns to Include</Label>
                  <div className="flex flex-wrap gap-2 mt-2 max-h-32 overflow-y-auto">
                    {(TABLE_COLUMN_OPTIONS[templateFormData.check_type] || []).map((col) => (
                      <Badge
                        key={col}
                        variant={templateFormData.table_columns?.includes(col) ? 'default' : 'outline'}
                        className={`cursor-pointer ${templateFormData.table_columns?.includes(col) ? 'bg-sky-600' : ''}`}
                        onClick={() => {
                          const current = templateFormData.table_columns || [];
                          if (current.includes(col)) {
                            setTemplateFormData({...templateFormData, table_columns: current.filter(c => c !== col)});
                          } else {
                            setTemplateFormData({...templateFormData, table_columns: [...current, col]});
                          }
                        }}
                      >
                        {col.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Preview */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Email Preview</Label>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handlePreviewTemplate}
                  disabled={!templateFormData.check_type}
                  data-testid="preview-template-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Generate Preview
                </Button>
              </div>

              {templatePreview ? (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-slate-100 p-3 border-b">
                    <p className="text-sm"><strong>Subject:</strong> {templatePreview.subject}</p>
                  </div>
                  <div 
                    className="p-4 bg-white max-h-[400px] overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(templatePreview.html) }}
                  />
                  {templatePreview.attachment_types?.length > 0 && (
                    <div className="bg-slate-50 p-3 border-t">
                      <p className="text-xs font-semibold mb-2">📎 Attachments:</p>
                      <div className="flex flex-wrap gap-2">
                        {templatePreview.attachment_types.map((at) => (
                          <Badge key={at} variant="outline" className="text-xs">
                            {ATTACHMENT_OPTIONS.find(o => o.value === at)?.label || at}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="border rounded-lg p-8 text-center bg-slate-50">
                  <Mail className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">Select a check type and click "Generate Preview" to see the email</p>
                </div>
              )}

              <div className="bg-blue-50 p-3 rounded-lg text-sm">
                <strong className="text-blue-700">Available Placeholders:</strong>
                <div className="mt-2 flex flex-wrap gap-1">
                  {['{{employee_name}}', '{{case_id}}', '{{company_name}}', '{{vendor_name}}', '{{university_name}}', '{{address}}', 
                    '{{city}}', '{{state}}', '{{contact_number}}', '{{tat_days}}', '{{days_left}}', '{{portal_link}}'].map((p) => (
                    <code key={p} className="bg-white px-2 py-0.5 rounded text-xs border">{p}</code>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => {
              setShowTemplateEditor(false);
              setSelectedTemplate(null);
              setTemplatePreview(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveTemplate} 
              className="bg-sky-600 hover:bg-sky-700"
              disabled={!templateFormData.name || !templateFormData.check_type}
              data-testid="save-template-btn"
            >
              {selectedTemplate?.is_default ? 'Create Custom Template' : 'Save Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplatesTab;
